/**
 * SingleCategoryUploadModal Component
 *
 * Modal for uploading selection documents to a single category.
 * Uploads files to Cloudinary, then stores URLs in Airtable via Firebase.
 *
 * @component
 * @version 1.0.0
 * @created 2025-12-31
 */

import React, { useState, useCallback } from "react";
import {
  FaTimes,
  FaCloudUploadAlt,
  FaFileAlt,
  FaUpload,
  FaSpinner,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useUploadSelectionDocuments } from "../../hooks/useSelections";
import useCurrentTeamMember from "../../hooks/useCurrentTeamMember";
import "./SingleCategoryUploadModal.css";

/**
 * Selection document categories matching Airtable schema.
 *
 * - `key` is the Airtable-side category identifier (camelCase) used by
 *   the `uploadSelectionDocument` backend function.
 * - `slug` is the normalized snake_case slug used for the Cloudinary
 *   folder structure and `category` context metadata (aligned with the
 *   slugs in SelectionDocsUploadModal).
 * - `fieldName` is the Airtable attachment field the upload writes to.
 */
const SELECTION_CATEGORIES = [
  {
    key: "roofGutter",
    slug: "roof_gutter",
    label: "Gutters",
    fieldName: "Roof/Gutter Documents",
  },
  {
    key: "doorsWindows",
    slug: "doors_windows",
    label: "Doors & Windows",
    fieldName: "Doors/Windows Documents",
  },
  {
    key: "garageDoor",
    slug: "garage_door",
    label: "Garage Door",
    fieldName: "Garage Door Documents",
  },
  {
    key: "fireplace",
    slug: "fireplace",
    label: "Fireplace",
    fieldName: "Fireplace Documents",
  },
  {
    key: "cabinets",
    slug: "cabinets",
    label: "Cabinets",
    fieldName: "Cabinets Documents",
  },
  {
    key: "countertopsSinks",
    slug: "countertops_sinks",
    label: "Countertops & Sinks",
    fieldName: "Countertops/Sinks Documents",
  },
  {
    key: "showersTubs",
    slug: "showers_tubs",
    label: "Shower Glass",
    fieldName: "Showers/Tubs Documents",
  },
  {
    key: "plumbingFixtures",
    slug: "plumbing_fixtures",
    label: "Plumbing Fixtures",
    fieldName: "Plumbing Fixture Documents",
  },
  {
    key: "lightingFixtures",
    slug: "lighting_fixtures",
    label: "Lighting Fixtures",
    fieldName: "Light Fixture Documents",
  },
  {
    key: "flooring",
    slug: "flooring",
    label: "Flooring",
    fieldName: "Flooring Documents",
  },
  {
    key: "tile",
    slug: "tile",
    label: "Tile",
    fieldName: "Tile Documents",
  },
  {
    key: "finalSelectionsPDF",
    slug: "final_selections_pdf",
    label: "Final Selections PDF",
    fieldName: "Final Selections Document",
  },
];

/**
 * SingleCategoryUploadModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback when modal closes
 * @param {string} props.projectId - Project record ID (required)
 * @param {string} [props.projectName] - Human-readable project name for Cloudinary `project_name` context
 * @param {string|null} [props.category] - Pre-selected category key (optional)
 * @param {Function} [props.onUploadComplete] - Callback after successful upload
 * @returns {JSX.Element|null} SingleCategoryUploadModal component
 */
function SingleCategoryUploadModal({
  isOpen,
  onClose,
  projectId,
  projectName = "",
  category = null,
  onUploadComplete,
}) {
  // ============ STATE ============
  const [selectedCategory, setSelectedCategory] = useState(
    category || SELECTION_CATEGORIES[0]?.key || "",
  );
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
    currentFile: "",
  });

  // ============ HOOKS ============
  const uploadMutation = useUploadSelectionDocuments();
  // Canonical RSH team member name for Cloudinary `uploaded_by_name`
  const { name: teamMemberName } = useCurrentTeamMember();

  // ============ HANDLERS ============

  /**
   * Handle file selection via input
   * @param {Event} e - Change event
   */
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
    setUploadError(null);
  };

  /**
   * Handle drag over event
   * @param {DragEvent} e - Drag event
   */
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  /**
   * Handle drag leave event
   * @param {DragEvent} e - Drag event
   */
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  /**
   * Handle file drop
   * @param {DragEvent} e - Drop event
   */
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    setSelectedFiles((prev) => [...prev, ...files]);
    setUploadError(null);
  }, []);

  /**
   * Remove a file from the selection
   * @param {number} index - File index to remove
   */
  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted size string
   */
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /**
   * Handle upload button click
   * Uploads all files to Cloudinary then stores URLs in Airtable
   */
  const handleUpload = async () => {
    if (!projectId) {
      setUploadError("No project selected");
      return;
    }

    if (selectedFiles.length === 0) {
      setUploadError("Please select at least one file");
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadProgress({
      current: 0,
      total: selectedFiles.length,
      currentFile: "",
    });

    const categoryInfo = SELECTION_CATEGORIES.find(
      (c) => c.key === selectedCategory,
    );

    try {
      let filesUploaded = 0;

      for (const file of selectedFiles) {
        setUploadProgress({
          current: filesUploaded,
          total: selectedFiles.length,
          currentFile: file.name,
        });

        await uploadMutation.mutateAsync({
          projectId,
          projectName,
          category: selectedCategory,
          categorySlug: categoryInfo?.slug || selectedCategory,
          categoryLabel: categoryInfo?.label || selectedCategory,
          fieldName: categoryInfo?.fieldName,
          uploadedByName: teamMemberName,
          file: file,
        });

        filesUploaded++;
        setUploadProgress({
          current: filesUploaded,
          total: selectedFiles.length,
          currentFile: file.name,
        });
      }

      // Success - call callback and close
      if (onUploadComplete) {
        onUploadComplete({
          category: selectedCategory,
          categoryLabel: categoryInfo?.label,
          fileCount: selectedFiles.length,
        });
      }
      onClose();
    } catch (error) {
      console.error("Upload error:", error);
      setUploadError(error.message || "Failed to upload files");
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (!isUploading) {
      setSelectedFiles([]);
      setUploadError(null);
      setUploadProgress({ current: 0, total: 0, currentFile: "" });
      onClose();
    }
  };

  /**
   * Handle overlay click
   * @param {Event} e - Click event
   */
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isUploading) {
      handleClose();
    }
  };

  // ============ COMPUTED VALUES ============
  const categoryLabel =
    SELECTION_CATEGORIES.find((c) => c.key === selectedCategory)?.label ||
    "Documents";

  // ============ RENDER ============
  if (!isOpen) return null;

  return (
    <div className="scum-modal-overlay" onClick={handleOverlayClick}>
      <div className="scum-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="scum-modal-header">
          <div className="scum-modal-header-left">
            <div className="scum-modal-header-icon">
              <FaUpload />
            </div>
            <h3>Upload Selection Documents</h3>
          </div>
          <button
            className="scum-modal-close"
            onClick={handleClose}
            disabled={isUploading}
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="scum-modal-body">
          {/* Category Selection */}
          <div className="scum-field-group">
            <label className="scum-label">Upload to Category:</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="scum-select"
              disabled={isUploading || Boolean(category)}
            >
              {SELECTION_CATEGORIES.map((cat) => (
                <option key={cat.key} value={cat.key}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Drop Zone */}
          <div
            className={`scum-dropzone ${isDragging ? "dragging" : ""} ${
              isUploading ? "disabled" : ""
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={isUploading ? undefined : handleDrop}
          >
            <input
              type="file"
              id="scum-doc-upload"
              className="scum-file-input"
              multiple
              onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              disabled={isUploading}
            />
            <label htmlFor="scum-doc-upload" className="scum-dropzone-content">
              <FaCloudUploadAlt className="scum-dropzone-icon" />
              <p className="scum-dropzone-text">
                Drag & drop files here or click to browse
              </p>
              <p className="scum-dropzone-hint">
                PDF, JPG, PNG, DOC - Max 10MB per file
              </p>
            </label>
          </div>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="scum-files">
              {selectedFiles.map((file, index) => (
                <div key={index} className="scum-file">
                  <FaFileAlt />
                  <span className="scum-file-name">{file.name}</span>
                  <span className="scum-file-size">
                    {formatFileSize(file.size)}
                  </span>
                  {!isUploading && (
                    <button
                      className="scum-file-remove"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="scum-progress">
              <div className="scum-progress-bar">
                <div
                  className="scum-progress-fill"
                  style={{
                    width: `${
                      uploadProgress.total > 0
                        ? (uploadProgress.current / uploadProgress.total) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>
              <p className="scum-progress-text">
                Uploading {uploadProgress.current} of {uploadProgress.total}...
                {uploadProgress.currentFile && (
                  <span className="scum-progress-file">
                    {uploadProgress.currentFile}
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Error Message */}
          {uploadError && (
            <div className="scum-error">
              <FaExclamationTriangle />
              <span>{uploadError}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="scum-modal-footer">
          <button
            className="scum-cancel-btn"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            className="scum-upload-btn"
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || isUploading}
          >
            {isUploading ? (
              <>
                <FaSpinner className="scum-spinner" /> Uploading...
              </>
            ) : (
              <>
                <FaUpload /> Upload to {categoryLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SingleCategoryUploadModal;
